import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { Tag } from '@app/_components/task/tag';
import { retry, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService } from '@app/_services';
import {SharedService} from '@app/_components/_shared/shared.service';

export interface CategoryCount
{
	name: string;
	count: number;
	color?: string; // R4 — hex color shared by all tags in this category
}

/** R4 — request body for PATCH /tags/categories/color */
export interface CategoryColorPayload
{
	name: string;
	color: string | null;
}

/** R6 — request body for PATCH /tags/{id}/breezeway-tag-id */
export interface BreezewayTagAssignPayload
{
	breezewayTagId: number;
	sourceBreezewayPropertyId: number;
}

@Injectable({ providedIn: 'root' })
export class TagService
{
	constructor
	(
		private http: HttpClient,
		private alertService: AlertService,
		private sharedService: SharedService
	) { }
	visibilities = [
		{ value: 1, name: 'Private' },
		{ value: 2, name: 'Team' },
		{ value: 3, name: 'Public' }
	];
	tagTypes = [
		{ value: 1, name: 'folder' },
		{ value: 2, name: 'context' },
		{ value: 3, name: 'goal' },
		{ value: 4, name: 'tag' },
		{ value: 5, name: 'project' },
		{ value: 6, name: 'phase' },
		{ value: 7, name: 'process' },
		// value 8 already exists in the data model — surfaces all our OP-wired tags
		// (Roll-Away Bed, Late Checkout, Early Check-In, etc.). Picking this on tag
		// create triggers TagController.save's Breezeway auto-create flow on prod.
		{ value: 8, name: 'reservation' },
		// value 9 is new — a label intended for property/short-term-rental tagging.
		// Has no auto-fire semantics (OPs apply to reservations, not properties)
		// and does NOT auto-create a Breezeway tag.
		{ value: 9, name: 'property' }
	];

	/**
	 * Returns true if the given tagType supports auto-firing from an order
	 * product (i.e., the "Triggered by Order Product" field is meaningful).
	 * Only reservation-typed tags (8) qualify — properties / folders / etc.
	 * aren't tied to OP additions.
	 */
	supportsOrderProductTrigger(tagType: number | null | undefined): boolean
	{
		return tagType === 8;
	}

	urlEndpoint = 'tags';

	add(tag: Tag): Observable<ApiResponse>
	{
		console.log('posting... ' + JSON.stringify(tag));
		return this.http.post<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }`, tag);
	}

	update(tag: Tag): Observable<ApiResponse>
	{
		return this.http.patch<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }`, tag);
	}

	delete(tagId: number): Observable<ApiResponse>
	{
		return this.http.delete<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/${ tagId }`);
	}

	findAll(): Observable<ApiResponse>
	{
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse>
	{

		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	findById(id: number): Observable<ApiResponse>
	{
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/${ id }`).pipe(retry({ count: 2, delay: 1500 }));
	}

	audit(id: number): Observable<ApiResponse>
	{
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/${ id }/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}

	/*
	 * Tag Group Category management
	 */

	getCategories(): Observable<ApiResponse>
	{
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/categories`).pipe(retry({ count: 2, delay: 1500 }));
	}

	renameCategory(oldName: string, newName: string): Observable<ApiResponse>
	{
		const body = { oldName: oldName, newName: newName };
		return this.http.patch<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/categories/rename`, body);
	}

	clearCategory(name: string): Observable<ApiResponse>
	{
		const encoded = encodeURIComponent(name);
		return this.http.delete<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/categories/${ encoded }`);
	}

	/* ------------------------------------------------------------------
	 * R4 — Per-category color picker
	 * ------------------------------------------------------------------
	 */

	/** Bulk-update color for every tag in `name`. color = null clears. */
	updateCategoryColor(payload: CategoryColorPayload): Observable<ApiResponse>
	{
		return this.http.patch<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/categories/color`, payload);
	}

	/* ------------------------------------------------------------------
	 * R5 — Tag delete with cascade preview
	 * ------------------------------------------------------------------
	 */

	/** Pre-delete impact check. Returns counts so UI can show "this tag is on N reservations" before deletion. */
	deletePreview(tagId: number): Observable<ApiResponse>
	{
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/${ tagId }/delete-preview`).pipe(retry({ count: 1, delay: 1000 }));
	}

	/* ------------------------------------------------------------------
	 * R6 — Admin-assignable breezewayTagId
	 * ------------------------------------------------------------------
	 */

	/** Manually map an existing Breezeway tag id to this KBM tag (validates against Breezeway). */
	updateBreezewayTagId(tagId: number, payload: BreezewayTagAssignPayload): Observable<ApiResponse>
	{
		return this.http.patch<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/${ tagId }/breezeway-tag-id`, payload);
	}

	/** Read-only proxy of Breezeway's per-property tag list. Powers the "Pick from Breezeway…" picker. */
	getBreezewayPropertyTags(breezewayPropertyId: number): Observable<ApiResponse>
	{
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/breezeway/properties/${ breezewayPropertyId }/tags`).pipe(retry({ count: 1, delay: 1000 }));
	}

	/**
	 * Create a Breezeway tag definition for a single local tagType=8 row that
	 * doesn't yet have a breezewayTagId. Backend POSTs to Breezeway and writes
	 * the returned id back to {@code task_tag.breezewayTagId}. Returns the
	 * resulting breezewayTagId or a "skipped"/"failed" status so the caller
	 * can surface the outcome.
	 */
	createBreezewayTagForTag(tagId: number): Observable<ApiResponse>
	{
		return this.http.post<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/${ tagId }/create-breezeway-tag`, {});
	}

	/**
	 * Batch counterpart of {@link createBreezewayTagForTag}. When tagIds is
	 * supplied, only those are processed (eligible-only); when omitted, the
	 * backend sweeps every tagType=8 row missing a breezewayTagId. Returns
	 * a per-tag report with status=created|skipped|failed.
	 */
	syncMissingBreezewayTags(tagIds?: number[]): Observable<ApiResponse>
	{
		let params = new HttpParams();
		if (tagIds && tagIds.length > 0) {
			params = params.set('tagIds', tagIds.join(','));
		}
		return this.http.post<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/sync-missing-breezeway`, {}, { params });
	}

	/* ------------------------------------------------------------------
	 * R7 — Tag history endpoints
	 * ------------------------------------------------------------------
	 */

	/** History of tag-add/remove events for a single reservation. */
	getReservationTagHistory(reservationId: number, limit = 50, offset = 0): Observable<ApiResponse>
	{
		const params = new HttpParams().set('limit', String(limit)).set('offset', String(offset));
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/reservations/${ reservationId }/tag-history`, { params });
	}

	/** Cross-source usage feed for a single tag (reservation assignments + folio-mapping rule changes). */
	getTagUsageHistory(tagId: number, limit = 50, offset = 0): Observable<ApiResponse>
	{
		const params = new HttpParams().set('limit', String(limit)).set('offset', String(offset));
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/${ tagId }/usage-history`, { params });
	}


	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any)
	{
		try
		{
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
			);

			let params = new HttpParams();

			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta)
			{
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++)
				{
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}
			// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
			if (globalFilter) { params = params.append('filter', globalFilter); }

			// multiselect or input or whatever... these work slick.
			if (filters)
			{
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++)
				{
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}
			return params;
		} catch (error)
		{
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}
}
